import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { loadObsState, saveObsState, clearObsState } from '../lib/obsStorage';

const ObsContext = createContext(null);

// Restored once, at module scope, so every piece of state starts from the
// same snapshot rather than each reading storage separately.
const restored = loadObsState();
const init = (key, fallback) => (restored?.[key] !== undefined ? restored[key] : fallback);

export function ObsProvider({ children }) {
  const [obs,          setObs]          = useState(() => init('obs', null));
  const [teacher,      setTeacher]      = useState(() => init('teacher', null));
  const [notes,        setNotes]        = useState(() => init('notes', []));
  const [seconds,      setSeconds]      = useState(() => init('seconds', 0));
  const [timerRunning, setTimerRunning] = useState(false);
  const [finalBest,      setFinalBest]      = useState(() => init('finalBest', []));
  const [finalBetter,    setFinalBetter]    = useState(() => init('finalBetter', []));
  const [statements,     setStatements]     = useState(() => init('statements', []));
  const [closeOutcome,   setCloseOutcome]   = useState(() => init('closeOutcome', null));
  const [nextStep,       setNextStep]       = useState(() => init('nextStep', ''));
  const [conclusions,    setConclusions]    = useState(() => init('conclusions', ''));
  const timerRef = useRef(null);

  // Keep the observation recoverable across a refresh. Without this, every
  // note taken so far is lost and the later screens have nothing to work from.
  useEffect(() => {
    saveObsState({
      obs, teacher, notes, seconds,
      finalBest, finalBetter,
      statements, closeOutcome, nextStep, conclusions,
    });
  }, [obs, teacher, notes, seconds,
      finalBest, finalBetter, statements, closeOutcome, nextStep, conclusions]);

  const startTimer = useCallback(() => {
    if (timerRef.current) return;
    setTimerRunning(true);
    timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setTimerRunning(false);
  }, []);

  const resetObs = useCallback(() => {
    stopTimer();
    setObs(null); setTeacher(null); setNotes([]); setSeconds(0);
    setFinalBest([]); setFinalBetter([]);
    setNextStep(''); setConclusions(''); setStatements([]); setCloseOutcome(null);
    clearObsState();
  }, [stopTimer]);

  const addNote = useCallback((note) => {
    // Keep the server's id — a saved note has to be addressable to be edited.
    setNotes(prev => [{ timestamp: seconds, ...note }, ...prev]);
  }, [seconds]);

  const updateNote = useCallback((id, patch) => {
    setNotes(prev => prev.map(n => (n.id === id ? { ...n, ...patch } : n)));
  }, []);

  /** Replace the note list wholesale — used when rehydrating from the server. */
  const setNotesFromServer = useCallback((list) => setNotes(list), []);

  return (
    <ObsContext.Provider value={{
      obs, setObs, teacher, setTeacher,
      notes, addNote, updateNote, setNotesFromServer,
      seconds, timerRunning, startTimer, stopTimer,
      finalBest, setFinalBest, finalBetter, setFinalBetter,
      nextStep, setNextStep, conclusions, setConclusions,
      statements, setStatements,
      closeOutcome, setCloseOutcome,
      resetObs,
    }}>
      {children}
    </ObsContext.Provider>
  );
}

export const useObs = () => useContext(ObsContext);
